/**
 * A MOLDURA da ficha da cliente: quem é a cliente, o atendimento aberto, a jornada, os produtos,
 * a vigência do Acompanhamento Anual e a barra das 16 abas. Porte de `portal/admin.html:2252-2295`
 * (HTML), `5871-5993` (abrir), `5995-6141` (produtos, vigência e abas), `5619-5649` (jornada) e
 * `9521-9545` (próximo passo). Linhas do inventário: 177 a 187.
 *
 * O ENDEREÇO é `/clientes/<id da cliente>?lead=<id do lead>` (Compromissos, Vendas e a lista abrem
 * assim). Sem `?lead=`, a moldura decide o atendimento com a regra da lista: nenhum abre sem lead,
 * um abre direto, dois ou mais perguntam "Qual atendimento?". `?de=<rota>` é para onde o "Voltar"
 * leva; sem ele, a lista de clientes, que reaparece como estava (`comum-ficha/volta.ts`).
 *
 * Cada aba recebe `idCliente` e `ficha={{ cliente, lead }}` (contrato em `comum-ficha/tipos.ts`), e
 * busca os próprios dados pelo CONTRATO DE CARGA (`comum-ficha/carregador.ts`): a moldura roda o
 * carregador da aba ANTES de mostrá-la, relê quando a pessoa toca de novo no botão aceso, e mantém
 * montada (escondida) toda aba que aderiu ao contrato, como o DOM único de `CARREGADOR_ABA_CLIENTE`
 * do legado (`5060-5091` e `5959-5968`).
 */

// Estado, efeito, efeito de layout, memória e retorno de função estável do React.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
// As peças do Chassi.
import { Aviso, Botao, Cartao, IconeVoltar, Selo } from '@complexity/ui';
// As leituras da lista, reusadas para montar a cliente com a mesma regra.
import { lerLeadsDaLista, lerLeadsParaEscolha, lerPerfilDaCliente, lerQuestionariosDaLista } from '@studiolu/dados/areas/clientes';
// As leituras e gravações da moldura.
import {
  gravarEtapaDaJornada,
  lerAbasDosProdutos,
  lerCadastroPeloQuestionario,
  lerJornada,
  lerLeadDaFicha,
  lerRoteiroProdutos,
  lerUltimoAcessoDaCliente,
  lerVigencia,
  salvarVigencia,
  type LeadDaFicha,
} from '@studiolu/dados/areas/ficha';
// A marca de lidas das mensagens, que o legado faz ao abrir a ficha (inventário 216).
import { listarMensagens, marcarMensagensComoLidas } from '@studiolu/dados/areas/ficha-mensagens';
// Tipos das linhas lidas aqui.
import type { LinhaRoteiroProdutos, LinhaVigenciaAcompanhamentoAnual } from '@studiolu/dados';
// Sessão, papel, fonte, rota e navegação.
import { usePainel } from '../../contexto.js';
// O endereço da ficha e o filtro da rota.
import { filtroDaRota } from '../../rotas.js';
// Telefone e último acesso com os formatos do legado.
import { formatarTelefone, formatarUltimoAcesso } from '../../comum/formatos.js';
// O modal do admin atual e a confirmação.
import { ModalLegado } from '../../comum/modal-legado.js';
import { useConfirmacao } from '../../comum/confirmacao.js';
// A regra da lista, para a cliente sair com os mesmos campos, e a ordem da escolha.
import { montarClientes, ordenarLeadsParaEscolha } from '../clientes/regras.js';
// O atalho do WhatsApp, a peça comum do admin (A5), com o mesmo desenho do legado.
import { LinkWhatsapp } from '../../comum/whatsapp.js';
// Os catálogos e as regras puras da ficha.
import { ABAS_DA_FICHA, JORNADA_ETAPA_LABEL, ORDEM_JORNADA, nomeDoProduto, type AbaDaFicha, type IdDaAba } from './comum-ficha/catalogo.js';
import {
  chavesDeParametro,
  dataBrDeDia,
  dataDeRenovacao,
  estadoDaVigencia,
  indiceDaEtapa,
  barraEnquantoCarrega,
  montarBarraDeAbas,
  ORDEM_DO_HTML,
  proximoProdutoSugerido,
} from './comum-ficha/regras.js';
// O destino do "Voltar", o endereço da ficha e de onde a ficha foi aberta.
import { abriuPelaAreaDeClientes, destinoDoVoltar, enderecoDaFicha } from './comum-ficha/volta.js';
import { escopoDaFicha, type ClienteDaFicha, type FichaAberta, type LeadAberto, type PropsDaAba } from './comum-ficha/tipos.js';
// O contrato de carga das abas: a moldura é quem oferece o contexto.
import { ContextoDaAba } from './comum-ficha/carregador.js';
// As 16 abas, pelos nomes combinados.
import { AbaAnaliseIa } from './abas/analise-ia.js';
import { AbaAnotacoesLu } from './abas/anotacoes-lu.js';
import { AbaArquivos } from './abas/arquivos.js';
import { AbaAtendimento } from './abas/atendimento.js';
import { AbaAvaliativo } from './abas/avaliativo.js';
import { AbaColoracao } from './abas/coloracao.js';
import { AbaDossie } from './abas/dossie.js';
import { AbaEmailAtendimento } from './abas/email-atendimento.js';
import { AbaListaDesejos } from './abas/lista-desejos.js';
import { AbaManualFoto } from './abas/manual-foto.js';
import { AbaMensagens } from './abas/mensagens.js';
import { AbaMilhoes } from './abas/milhoes.js';
import { AbaPersonalidade } from './abas/personalidade.js';
import { AbaQuestionario } from './abas/questionario.js';
import { AbaRadar } from './abas/radar.js';
import { AbaTimeline } from './abas/timeline.js';

/** O que a moldura sabe de si, do endereço até a ficha pronta. */
type Estado =
  // Lendo a cliente e o lead.
  | { readonly tipo: 'carregando' }
  // A cliente não está no portal (texto do legado, `admin.html:6946`).
  | { readonly tipo: 'sem-cadastro' }
  // Dois ou mais atendimentos e nenhum escolhido no endereço.
  | { readonly tipo: 'escolher'; readonly cliente: ClienteDaFicha; readonly leads: readonly LeadAberto[] }
  // A ficha aberta.
  | { readonly tipo: 'aberta'; readonly ficha: FichaAberta };

/**
 * A ordem dos 16 botões como a última ficha os deixou, guardada FORA do componente de propósito.
 *
 * DEFEITO DO LEGADO, REPRODUZIDO IGUAL (achado 1 da verificação da A2; a regra e as linhas do
 * legado estão em `montarBarraDeAbas`, `comum-ficha/regras.ts`). No legado a barra é um só
 * elemento do começo ao fim da página, e o `appendChild` que a reordena não é desfeito ao abrir
 * outra cliente. Aqui a ficha desmonta a cada troca, e o módulo é o que dura a sessão da página:
 * recarregar a página volta à ordem do HTML, como no legado.
 */
let ordemDosBotoes: readonly IdDaAba[] = ORDEM_DO_HTML;
// Os botões que a última ficha deixou à mostra (o `display` de cada um no legado); nulo na primeira.
let visiveisDaAnterior: readonly IdDaAba[] | null = null;

/**
 * Lê a cliente com os MESMOS campos que a lista monta (`admin.html:3603-3613`): as três leituras
 * da lista, com a lista de uma cliente só. Sem perfil, o cadastro pelo questionário, como
 * `abrirClienteDoLead` (`admin.html:6937-6950`). `null` quando nenhum dos dois existe.
 */
async function lerClienteDaFicha(dados: ReturnType<typeof usePainel>['dados'], userId: string): Promise<ClienteDaFicha | null> {
  const perfil = await lerPerfilDaCliente(dados, userId);
  if (perfil.ok && perfil.dados) {
    const questionarios = await lerQuestionariosDaLista(dados, [userId]);
    const leads = await lerLeadsDaLista(dados, [userId]);
    const { clientes } = montarClientes([perfil.dados], questionarios.ok ? questionarios.dados : [], leads.ok ? leads.dados : []);
    const c = clientes[0]!;
    return { user_id: c.user_id, nome_completo: c.nome_completo, email: c.email, telefone: c.telefone };
  }
  const cadastro = await lerCadastroPeloQuestionario(dados, userId);
  if (cadastro.ok && cadastro.dados) return cadastro.dados;
  return null;
}

/**
 * O lead como o CAMINHO DE ABERTURA o entrega no legado.
 *
 * Pelo Kanban (`admin.html:6511`) e pelo compromisso (`4540`) o legado passa a linha inteira do
 * lead, com a origem; pela lista de clientes ele passa só quatro colunas (`5877-5885`). A ficha
 * relê sempre as mesmas colunas (o endereço próprio não guarda objeto), então quem reproduz a
 * diferença é esta função: vindo da área Clientes, a origem some, e o Histórico escreve "Lead
 * criada" sem ela, como no admin atual.
 */
function leadDoCaminho(lead: LeadDaFicha, veioDaAreaDeClientes: boolean): LeadAberto {
  const base: LeadAberto = {
    id: lead.id,
    produtos: lead.produtos ?? [],
    status: lead.status,
    data_criacao: lead.data_criacao,
  };
  return veioDaAreaDeClientes ? base : { ...base, origem: lead.origem };
}

export function TelaFichaCliente({ idCliente }: { readonly idCliente: string }) {
  const { dados, acesso, rota, navegar } = usePainel();
  const filtro = filtroDaRota(rota);
  // O lead pedido no endereço, quando é um número.
  const leadPedido = filtro.get('lead');
  const idLead = leadPedido && /^\d+$/.test(leadPedido) ? Number(leadPedido) : null;
  // Para onde o "Voltar" leva: a área de origem, ou a lista de clientes como estava.
  const destinoVoltar = destinoDoVoltar(filtro.get('de'));
  // De onde a ficha foi aberta, que decide se o lead chega com origem (ver `leadDoCaminho`).
  const veioDaAreaDeClientes = abriuPelaAreaDeClientes(filtro.get('de'));
  const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' });

  // Abre a cliente e decide o atendimento, a cada troca de cliente ou de lead no endereço.
  useEffect(() => {
    let vivo = true;
    setEstado({ tipo: 'carregando' });
    void (async () => {
      const cliente = await lerClienteDaFicha(dados, idCliente);
      if (!vivo) return;
      if (!cliente) {
        setEstado({ tipo: 'sem-cadastro' });
        return;
      }
      // Com o lead no endereço: ele, se for desta cliente.
      if (idLead !== null) {
        const lead = await lerLeadDaFicha(dados, idCliente, idLead);
        if (!vivo) return;
        if (lead.ok && lead.dados) {
          setEstado({ tipo: 'aberta', ficha: { cliente, lead: leadDoCaminho(lead.dados, veioDaAreaDeClientes) } });
          return;
        }
      }
      // Sem lead no endereço (ou lead que não é dela): a regra da lista (`admin.html:5875-5889`).
      const leads = await lerLeadsParaEscolha(dados, idCliente);
      if (!vivo) return;
      if (!leads.ok) console.error('Erro ao carregar leads da cliente:', leads.erro);
      const lista = leads.ok ? leads.dados.map((l) => ({ ...l, produtos: l.produtos ?? [] })) : [];
      if (lista.length === 0) setEstado({ tipo: 'aberta', ficha: { cliente, lead: null } });
      else if (lista.length === 1) setEstado({ tipo: 'aberta', ficha: { cliente, lead: lista[0]! } });
      else setEstado({ tipo: 'escolher', cliente, leads: ordenarLeadsParaEscolha(lista) });
    })();
    return () => {
      vivo = false;
    };
  }, [dados, idCliente, idLead, veioDaAreaDeClientes]);

  // O "Voltar" do legado é um `span` de propósito, para "Apenas Consulta" não perdê-lo (`2254-2256`).
  const voltar = (
    <Botao variante="texto" tamanho="sm" className="self-start" onClick={() => navegar(destinoVoltar)}>
      <IconeVoltar aria-hidden="true" width={16} height={16} />
      Voltar
    </Botao>
  );

  if (estado.tipo === 'carregando') return <div className="flex flex-col gap-cx-4">{voltar}</div>;

  if (estado.tipo === 'sem-cadastro') {
    return (
      <div className="flex flex-col gap-cx-4">
        {voltar}
        <Aviso tom="alerta" titulo="Aviso">
          Não encontrei o cadastro dessa cliente no portal. Ela ainda pode não ter respondido o questionário.
        </Aviso>
      </div>
    );
  }

  if (estado.tipo === 'escolher') {
    const { cliente, leads } = estado;
    return (
      <div className="flex flex-col gap-cx-4">
        {voltar}
        {/* O mesmo modal da lista; fechar sem escolher volta para onde a pessoa estava. */}
        <ModalLegado aberto aoFechar={() => navegar(destinoVoltar)} titulo="Qual atendimento?" testid="modal-selecao-lead-ficha">
          <div className="flex flex-col gap-cx-3">
            <p className="text-cx-sm text-texto-suave">{cliente.nome_completo || ''}</p>
            {leads.map((lead) => {
              const finalizado = lead.status === 'finalizada';
              const produtos = lead.produtos.map(nomeDoProduto).join(', ') || '(sem produto definido)';
              // O atendimento escolhido, com a mesma origem, para o "Voltar" continuar valendo.
              const destino = enderecoDaFicha(cliente.user_id, { lead: lead.id, de: filtro.get('de') ?? undefined });
              return (
                <Cartao key={lead.id} destino={destino} aoNavegar={navegar} nomeAcessivel={`${produtos}: ${finalizado ? 'Finalizado' : 'Em andamento'}`}>
                  <div className={`text-cx-sm font-medium ${finalizado ? 'text-texto-suave' : 'text-texto'}`}>{produtos}</div>
                  <div className="text-cx-xs text-texto-suave">{finalizado ? 'Finalizado' : 'Em andamento'}</div>
                </Cartao>
              );
            })}
          </div>
        </ModalLegado>
      </div>
    );
  }

  // A chave remonta a ficha inteira ao trocar de cliente ou de lead: nada da anterior fica na tela,
  // que é o defeito que o legado combate travando os botões de salvar (`admin.html:5954-5968`).
  return (
    <FichaAbertaNaTela
      key={`${estado.ficha.cliente.user_id}:${estado.ficha.lead?.id ?? 'sem-lead'}`}
      ficha={estado.ficha}
      voltar={voltar}
      papel={acesso.papel}
    />
  );
}

/** A ficha com cliente e lead já decididos. */
function FichaAbertaNaTela({
  ficha,
  voltar,
  papel,
}: {
  readonly ficha: FichaAberta;
  readonly voltar: ReactNode;
  readonly papel: string;
}) {
  const { dados } = usePainel();
  const { confirmar, alertar, dialogo } = useConfirmacao();
  const { cliente, lead } = ficha;
  // O escopo de toda leitura e gravação desta ficha.
  const escopo = useMemo(() => escopoDaFicha(ficha), [ficha]);
  // "Apenas Consulta" não vê botão de ação nem edita (`admin.html:3392-3397`).
  const somenteLeitura = papel === 'consulta';
  // Os produtos do lead aberto, e só dele (`carregarProdutosLead`, `admin.html:5995-6000`).
  const produtos = useMemo(() => lead?.produtos ?? [], [lead]);
  const finalizado = lead?.status === 'finalizada';

  // O texto do último acesso; vazio até a leitura voltar, como o `textContent = ''` do legado.
  const [ultimoAcesso, setUltimoAcesso] = useState('');
  // A etapa atual da jornada; `null` até a leitura voltar.
  const [etapa, setEtapa] = useState<string | null>(null);
  // O roteiro de produtos, para o próximo passo.
  const [roteiro, setRoteiro] = useState<readonly LinhaRoteiroProdutos[]>([]);
  // A vigência do Acompanhamento Anual: `undefined` sem ler, `null` sem registro.
  const [vigencia, setVigencia] = useState<LinhaVigenciaAcompanhamentoAnual | null | undefined>(undefined);
  // A barra até os vínculos voltarem: a da ficha anterior, como o legado (`barraEnquantoCarrega`).
  const [inicio] = useState(() => barraEnquantoCarrega(ordemDosBotoes, visiveisDaAnterior));
  // As abas visíveis, na ordem da barra.
  const [abas, setAbas] = useState<readonly AbaDaFicha[]>(inicio.visiveis);
  // O botão aceso e o conteúdo na tela. São dois porque no legado podem divergir (`montarBarraDeAbas`).
  const [acesa, setAcesa] = useState<IdDaAba>(inicio.acesa);
  const [conteudo, setConteudo] = useState<IdDaAba>(inicio.conteudo);
  /**
   * As abas MONTADAS: a que está na tela e as que aderiram ao contrato de carga e já foram abertas.
   *
   * No legado as 16 abas existem no DOM o tempo todo, e trocar de aba só troca uma classe
   * (`admin.html:5088-5091`): rascunho, anexo, arquivo escolhido e linha de status continuam lá
   * (`5959-5968`, transversal T3 da verificação da A3). Aqui a aba que aderiu ao contrato fica
   * montada e escondida, que é o mesmo efeito; a que ainda não aderiu desmonta ao sair, como antes,
   * porque ela só lê na montagem e ficar montada a deixaria sem reler.
   */
  const [montadas, setMontadas] = useState<readonly IdDaAba[]>([inicio.conteudo]);
  // Quantos toques cada botão recebeu nesta ficha: é a "carga" que o contrato entrega à aba, e cada
  // número novo é um pedido de leitura, inclusive no botão que já está aceso (`5086`).
  const [cargas, setCargas] = useState<Readonly<Partial<Record<IdDaAba, number>>>>({});
  // Quem aderiu ao contrato, registrado pela própria aba na montagem.
  const aderidas = useRef<Set<IdDaAba>>(new Set());
  // As abas que um toque acabou de montar e que ainda não se sabe se aderiram.
  const esperandoMontagem = useRef<Set<IdDaAba>>(new Set());
  // As montadas agora, para o toque decidir sem depender do render em que ele nasceu.
  const montadasAgora = useRef<readonly IdDaAba[]>(montadas);
  // Os dois também em referência: o efeito da abertura precisa do valor de QUANDO os vínculos chegam
  // (a pessoa pode ter tocado numa aba durante a carga), e não do valor de quando ele começou.
  const agora = useRef({ acesa: inicio.acesa, conteudo: inicio.conteudo });
  // Mostra uma aba: botão, conteúdo e a referência juntos, para nunca divergirem entre si.
  const mostrarAba = useCallback((novaAcesa: IdDaAba, novoConteudo: IdDaAba) => {
    agora.current = { acesa: novaAcesa, conteudo: novoConteudo };
    setAcesa(novaAcesa);
    setConteudo(novoConteudo);
    setMontadas((m) => {
      // Ficam a aba que vai aparecer, as que aderiram ao contrato e as que um toque está montando.
      const ficam = m.filter((id) => id === novoConteudo || aderidas.current.has(id) || esperandoMontagem.current.has(id));
      return ficam.includes(novoConteudo) ? ficam : [...ficam, novoConteudo];
    });
  }, []);

  /**
   * O TOQUE num botão da barra, que é o `click` do legado (`admin.html:5085-5091`): ele pede a
   * leitura e só mostra a aba quando ela volta.
   *
   * Aba que aderiu ao contrato: o pedido é o número da carga, e quem mostra é o aviso de `carregou`.
   * Aba que ainda não aderiu: aparece na hora, como antes do contrato, porque ela lê ao montar.
   */
  const tocarAba = useCallback(
    (id: IdDaAba) => {
      setCargas((c) => ({ ...c, [id]: (c[id] ?? 0) + 1 }));
      if (aderidas.current.has(id)) return;
      if (montadasAgora.current.includes(id)) {
        mostrarAba(id, id);
        return;
      }
      // Ainda não montada: monta escondida agora, e o efeito de layout decide o que fazer quando
      // souber se ela aderiu (o registro do contrato acontece na montagem dela).
      esperandoMontagem.current.add(id);
      setMontadas((m) => (m.includes(id) ? m : [...m, id]));
    },
    [mostrarAba],
  );

  // A aba avisa que adere ao contrato; a devolução desfaz o registro quando ela desmonta.
  const registrar = useCallback((id: IdDaAba) => {
    aderidas.current.add(id);
    return () => {
      aderidas.current.delete(id);
    };
  }, []);

  // A aba avisa que a leitura pedida por um toque terminou: é aqui que ela aparece.
  const carregou = useCallback(
    (id: IdDaAba, carga: number) => {
      // Carga zero é a leitura da montagem sem toque nenhum: a aba que abriu com a ficha já está
      // na tela, e o legado também não a mostra de novo depois do carregador da abertura (`5987`).
      if (carga === 0) return;
      mostrarAba(id, id);
    },
    [mostrarAba],
  );

  // Depois de cada montagem: guarda a lista atual e resolve as abas que não aderiram ao contrato.
  useLayoutEffect(() => {
    montadasAgora.current = montadas;
    for (const id of [...esperandoMontagem.current]) {
      // Ainda não entrou no DOM neste commit: espera o próximo.
      if (!montadas.includes(id)) continue;
      esperandoMontagem.current.delete(id);
      // Aderiu: quem mostra é o aviso da leitura, e até lá a aba anterior continua na tela.
      if (aderidas.current.has(id)) continue;
      mostrarAba(id, id);
    }
  }, [montadas, mostrarAba]);

  // A jornada, lida de novo quando uma gravação falha (`carregarJornada`, `admin.html:5629-5636`).
  const carregarJornada = useCallback(async () => {
    const r = await lerJornada(dados, escopo);
    if (!r.ok) console.error('Erro ao carregar jornada:', r.erro);
    // Sem registro, "Diagnóstico Inicial" (`data?.etapa || 'diagnostico_inicial'`).
    setEtapa((r.ok ? r.dados?.etapa : null) || 'diagnostico_inicial');
  }, [dados, escopo]);

  // O que a abertura carrega, na ordem do legado (`admin.html:5949` e `5987-5993`).
  useEffect(() => {
    let vivo = true;
    void (async () => {
      // Último acesso: só lê (`admin.html:5935-5941`).
      const acessoR = await lerUltimoAcessoDaCliente(dados, cliente.user_id);
      if (!vivo) return;
      setUltimoAcesso(
        acessoR.ok && acessoR.dados?.acessado_em
          ? formatarUltimoAcesso(acessoR.dados.acessado_em)
          : 'Cliente ainda não acessou o portal.',
      );
      await carregarJornada();
      // O roteiro que o legado lê na entrada do painel (`admin.html:3505`).
      const roteiroR = await lerRoteiroProdutos(dados);
      if (!vivo) return;
      setRoteiro(roteiroR.ok ? roteiroR.dados : []);
      // A vigência, só com o Acompanhamento Anual (`admin.html:6028-6040`).
      if (produtos.includes('acompanhamento_anual')) {
        const vig = await lerVigencia(dados, escopo);
        if (!vivo) return;
        if (!vig.ok) console.error('Erro ao carregar vigência do acompanhamento anual:', vig.erro);
        setVigencia(vig.ok ? vig.dados : null);
      }
      // As abas pelo vínculo produto x aba (`admin.html:6086-6141`); sem produto, nem consulta.
      const chaves = chavesDeParametro(produtos);
      const vinculos = chaves.length > 0 ? await lerAbasDosProdutos(dados, chaves) : null;
      if (!vivo) return;
      // A barra parte da ordem que a ficha anterior deixou, e deixa a dela para a próxima. A leitura
      // e a escrita ficam juntas, depois do `vivo`: no modo estrito do React o efeito roda duas
      // vezes, e só a execução viva pode mexer na ordem, senão a segunda partiria da ordem nova.
      const barra = montarBarraDeAbas(produtos, vinculos?.ok ? vinculos.dados : [], ordemDosBotoes, agora.current);
      ordemDosBotoes = barra.ordem;
      visiveisDaAnterior = barra.visiveis.map((aba) => aba.id);
      setAbas(barra.visiveis);
      // O legado não troca a aba aqui: quando o botão aceso ficou escondido, ele CLICA no primeiro
      // visível (`primeiroVisivel.click()`, `admin.html:6138`), e o clique lê antes de mostrar.
      // Sem clique, botão e conteúdo ficam como estão, mesmo quando não batem entre si.
      if (barra.clique) tocarAba(barra.clique);
      // As mensagens da cliente viram lidas ao abrir a ficha, como o pré-carregamento da aba
      // Mensagens faz no legado (`admin.html:5768` e `5809-5811`, inventário 216).
      const msgs = await listarMensagens(dados, escopo);
      if (!vivo || !msgs.ok) return;
      const naoLidas = (msgs.dados ?? []).filter((m) => m.remetente !== 'consultora' && !m.lida_em);
      if (naoLidas.length > 0) {
        await marcarMensagensComoLidas(
          dados,
          naoLidas.map((m) => m.id),
          new Date().toISOString(),
        );
      }
    })();
    return () => {
      vivo = false;
    };
  }, [dados, escopo, cliente.user_id, produtos, carregarJornada, tocarAba]);

  // Clicar numa etapa grava na hora (`definirEtapa`, `admin.html:5638-5646`).
  const definirEtapa = async (nova: string) => {
    setEtapa(nova);
    const r = await gravarEtapaDaJornada(dados, escopo, nova, new Date().toISOString());
    if (!r.ok) {
      await alertar(`Erro ao atualizar etapa: ${r.erro.mensagem}`);
      await carregarJornada();
    }
  };

  // Grava a vigência (`salvarVigenciaAcompanhamentoAnual`, `admin.html:6071-6084`).
  const gravarVigencia = async (dataInicio: string) => {
    const r = await salvarVigencia(dados, escopo, {
      data_inicio: dataInicio,
      data_renovacao: dataDeRenovacao(dataInicio),
      lembrete_enviado_em: null,
      atualizado_em: new Date().toISOString(),
    });
    if (!r.ok) {
      await alertar(`Erro ao salvar vigência: ${r.erro.mensagem}`);
      return;
    }
    setVigencia(r.dados);
  };

  // O próximo produto sugerido, só com produto no lead (`renderizarAvisoProximoProduto`, `9532-9545`).
  const proximo = produtos.length > 0 ? proximoProdutoSugerido(roteiro, produtos) : null;

  return (
    <div className="flex flex-col gap-cx-4">
      {voltar}

      <header className="flex flex-col items-center gap-cx-1 text-center">
        <p className="text-cx-xs uppercase tracking-wide text-texto-suave">Cliente</p>
        <h2 className="text-cx-2xl font-semibold text-texto">{cliente.nome_completo || '(sem nome)'}</h2>
        <p className="break-words text-cx-sm text-texto-suave">
          {cliente.email || ''}
          {cliente.telefone ? ` · ${formatarTelefone(cliente.telefone)}` : ''}
          {cliente.telefone && <LinkWhatsapp telefone={cliente.telefone} />}
        </p>
        <p className="text-cx-xs text-texto-suave" data-testid="ultimo-acesso-cliente">
          {ultimoAcesso}
        </p>
      </header>

      {/* A barra da jornada. As etapas são botões, e "Apenas Consulta" não vê botão de ação
          (`admin.html:3395`): no legado a barra fica vazia para ela, e aqui ela não é desenhada. */}
      {!somenteLeitura && etapa !== null && (
        <BarraDaJornada etapa={etapa} aoEscolher={(e) => void definirEtapa(e)} />
      )}

      {produtos.length > 0 && (
        // O cartão dos produtos do lead aberto (`admin.html:6003-6011`).
        <Cartao>
          <div className="flex flex-col gap-cx-2" data-testid="produtos-cliente">
            <div className="text-cx-xs font-medium uppercase tracking-wide text-texto-suave">
              {finalizado ? 'Finalizado' : 'Contratado'}
            </div>
            <div className="flex flex-wrap gap-cx-1">
              {produtos.map((p) => (
                <Selo key={p} tom={finalizado ? 'neutro' : 'marca'}>
                  {nomeDoProduto(p)}
                </Selo>
              ))}
            </div>
          </div>
        </Cartao>
      )}

      {proximo && (
        <p className="rounded-cx-md border border-borda px-cx-4 py-cx-3 text-cx-sm text-texto" data-testid="aviso-proximo-produto">
          Próximo passo sugerido: <strong className="font-semibold">{nomeDoProduto(proximo)}</strong>
        </p>
      )}

      {produtos.includes('acompanhamento_anual') && vigencia !== undefined && (
        <Vigencia
          vigencia={vigencia}
          somenteLeitura={somenteLeitura}
          aoDefinir={async (data) => {
            // Sem data, o aviso do legado (`admin.html:6047`).
            if (!data) {
              await alertar('Escolha uma data.');
              return;
            }
            await gravarVigencia(data);
          }}
          aoRenovar={async () => {
            const sim = await confirmar('Renovar o Acompanhamento Anual a partir de hoje? Isso reinicia o ciclo de 1 ano.');
            // "Hoje" em UTC, como `new Date().toISOString().slice(0, 10)` do legado (`6067`).
            if (sim) await gravarVigencia(new Date().toISOString().slice(0, 10));
          }}
        />
      )}

      {/* A barra e o conteúdo aparecem junto com a ficha, como no legado (`admin.html:5951`). */}
      <nav aria-label="Seções do atendimento" className="flex flex-wrap gap-cx-1 border-b border-borda pb-cx-2">
        {abas.map((aba) => (
          <Botao
            key={aba.id}
            variante={aba.id === acesa ? 'primario' : 'fantasma'}
            tamanho="sm"
            aria-current={aba.id === acesa ? 'true' : undefined}
            data-aba={aba.id}
            // O clique lê os dados da aba e só então acende o botão e mostra o conteúdo, e os dois
            // voltam a bater (`admin.html:5086-5091`). Tocar no botão já aceso relê.
            onClick={() => tocarAba(aba.id)}
          >
            {aba.rotulo}
          </Botao>
        ))}
      </nav>
      {/*
        As abas montadas. Só a do conteúdo aparece; as outras ficam escondidas com o que a pessoa
        digitou, como o DOM único do legado.

        O ATRIBUTO É `data-conteudo`, e não `data-aba`: `data-aba` é do BOTÃO da barra, e repeti-lo
        aqui deixou `[data-aba="..."]` casando dois elementos e quebrou a suíte da A3 (transversal
        T4 da verificação dela). O `data-testid` fica só na aba visível, para quem procura "a aba
        na tela" continuar achando uma só.
      */}
      {montadas.map((id) => (
        <section
          key={id}
          hidden={id !== conteudo}
          aria-label={ABAS_DA_FICHA.find((a) => a.id === id)?.rotulo}
          data-testid={id === conteudo ? 'conteudo-da-aba' : undefined}
          data-conteudo={id}
        >
          <ContextoDaAba.Provider value={{ id, carga: cargas[id] ?? 0, ativa: id === conteudo, registrar, carregou }}>
            <ConteudoDaAba id={id} ficha={ficha} />
          </ContextoDaAba.Provider>
        </section>
      ))}

      {dialogo}
    </div>
  );
}

/** A barra das cinco etapas: as anteriores feitas, a atual ativa (`renderizarJornada`, `5620-5627`). */
function BarraDaJornada({ etapa, aoEscolher }: { readonly etapa: string; readonly aoEscolher: (etapa: string) => void }) {
  const atual = indiceDaEtapa(etapa);
  return (
    <div className="flex flex-wrap justify-center gap-cx-1" data-testid="jornada-barra">
      {ORDEM_JORNADA.map((chave, indice) => {
        const concluida = indice < atual;
        const ativa = indice === atual;
        return (
          <Botao
            key={chave}
            variante="fantasma"
            tamanho="sm"
            aria-current={ativa ? 'step' : undefined}
            data-etapa={chave}
            data-estado={ativa ? 'ativa' : concluida ? 'concluida' : 'pendente'}
            onClick={() => aoEscolher(chave)}
          >
            {/* A bolinha: cheia nas feitas e na atual, vazia nas que faltam. */}
            <span
              aria-hidden="true"
              className={`inline-block h-cx-3 w-cx-3 rounded-cx-full border ${
                concluida || ativa ? 'border-marca bg-marca' : 'border-borda-forte bg-transparent'
              }`}
            />
            <span className={ativa ? 'font-semibold text-texto' : 'text-texto-suave'}>{JORNADA_ETAPA_LABEL[chave]}</span>
          </Botao>
        );
      })}
    </div>
  );
}

/** O bloco "Acompanhamento Anual" (`renderizarVigenciaAcompanhamentoAnual`, `admin.html:6042-6069`). */
function Vigencia({
  vigencia,
  somenteLeitura,
  aoDefinir,
  aoRenovar,
}: {
  readonly vigencia: LinhaVigenciaAcompanhamentoAnual | null;
  readonly somenteLeitura: boolean;
  readonly aoDefinir: (data: string) => Promise<void>;
  readonly aoRenovar: () => Promise<void>;
}) {
  // A data escolhida antes de "Definir início".
  const [data, setData] = useState('');
  return (
    <div className="flex flex-wrap items-center gap-cx-3 rounded-cx-md border border-borda px-cx-4 py-cx-3" data-testid="vigencia-acompanhamento">
      <strong className="text-cx-sm font-semibold text-texto">Acompanhamento Anual</strong>
      {vigencia === null ? (
        <>
          {/* O campo de data do legado não tem rótulo visível (`admin.html:6045`); o nome vai para
              o leitor de tela. Ele usa a classe de campo do Chassi, e não uma aparência própria. */}
          <input
            type="date"
            className="cx-campo w-auto"
            aria-label="Início do Acompanhamento Anual"
            value={data}
            disabled={somenteLeitura}
            onChange={(e) => setData(e.target.value)}
          />
          {!somenteLeitura && (
            <Botao variante="secundario" tamanho="sm" onClick={() => void aoDefinir(data)}>
              Definir início
            </Botao>
          )}
        </>
      ) : (
        <LinhaDaVigencia vigencia={vigencia} somenteLeitura={somenteLeitura} aoRenovar={aoRenovar} />
      )}
    </div>
  );
}

/** A linha com início e renovação, na cor do prazo, e o "Renovar agora". */
function LinhaDaVigencia({
  vigencia,
  somenteLeitura,
  aoRenovar,
}: {
  readonly vigencia: LinhaVigenciaAcompanhamentoAnual;
  readonly somenteLeitura: boolean;
  readonly aoRenovar: () => Promise<void>;
}) {
  // Vencida e próxima dos 45 dias são as duas em vermelho; só o peso muda (`admin.html:627-628`).
  const { estado } = estadoDaVigencia(vigencia.data_renovacao, new Date());
  const cor = estado === 'vencida' ? 'font-bold text-erro' : estado === 'proxima' ? 'font-semibold text-erro' : 'text-texto';
  return (
    <>
      <span className={`text-cx-sm ${cor}`} data-estado={estado}>
        {`Início: ${dataBrDeDia(vigencia.data_inicio)} · Renova em: ${dataBrDeDia(vigencia.data_renovacao)}`}
      </span>
      {!somenteLeitura && (
        <Botao variante="secundario" tamanho="sm" onClick={() => void aoRenovar()}>
          Renovar agora
        </Botao>
      )}
    </>
  );
}

/**
 * As 16 abas pelo id. O tipo `ComponentType<PropsDaAba>` aceita também as abas que ainda
 * recebem só `idCliente`: um componente que pede menos propriedades serve onde se passam mais,
 * e a barra não quebra enquanto cada frente migra a sua aba.
 */
const ABAS: Readonly<Record<IdDaAba, ComponentType<PropsDaAba>>> = {
  'aba-questionario': AbaQuestionario,
  'aba-personalidade': AbaPersonalidade,
  'aba-radar': AbaRadar,
  'aba-arquivos': AbaArquivos,
  'aba-atendimento': AbaAtendimento,
  'aba-milhoes': AbaMilhoes,
  'aba-coloracao': AbaColoracao,
  'aba-lista-desejos': AbaListaDesejos,
  'aba-anotacoes-lu': AbaAnotacoesLu,
  'aba-avaliativo': AbaAvaliativo,
  'aba-dossie': AbaDossie,
  'aba-analise-ia': AbaAnaliseIa,
  'aba-mensagens': AbaMensagens,
  'aba-email-atendimento': AbaEmailAtendimento,
  'aba-manual-foto': AbaManualFoto,
  'aba-timeline': AbaTimeline,
};

/** A aba ativa, com as duas propriedades combinadas. */
function ConteudoDaAba({ id, ficha }: { readonly id: IdDaAba; readonly ficha: FichaAberta }) {
  const Aba = ABAS[id];
  return <Aba idCliente={ficha.cliente.user_id} ficha={ficha} />;
}
